import { useTranslation } from "react-i18next";
import { useState } from "react";
import createDOMPurify from "dompurify";
import { useSelector } from "react-redux";

import EditIcon from "@/assets/icons/edit.svg?react";
import LinkIcon from "@/assets/icons/link.svg?react";
import EmailIcon from "@/assets/icons/email.svg?react";
import PinIcon from "@/assets/icons/pin.svg?react";
import WorldIcon from "@/assets/icons/world.svg?react";
import PhoneIcon from "@/assets/icons/phone.svg?react";
import LanguageIcon from "@/assets/icons/language.svg?react";
import IdCardIcon from "@/assets/icons/id-card.svg?react";
import FlagIcon from "@/assets/icons/flag.svg?react";
import ParentOrganizationIcon from "@/assets/icons/building.svg?react";
import placeholderImage from "@/assets/images/placeholderImage.png";
import {
  useGetAchievementsQuery,
  useGetCommunityRolesQuery,
  useGetCountryByIdQuery,
  useGetLanguageByIdQuery,
} from "@/redux/service";
import BaseDivider from "@/components_v2/Divider/BaseDivider";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import BaseBadge from "@/components_v2/Badge/BaseBadge";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import DotIcon from "@/assets/icons/dot.svg?react";
import {
  Chapters,
  OrganizationCommunicationStatuses,
  Roles,
} from "@/shared/utils/constants";
import BaseAvatar from "@/components_v2/Avatar/BaseAvatar";
import BaseButton from "@/components_v2/Button/BaseButton";
import { OrganizationDetailsContentProps } from "@/features/organizations/profile/types";
import { hasExternalId } from "@/redux/organizations/typeGuards";
import { useOrganizationPermissions } from "@/features/organizations/hooks/useOrganizationPermissions";
import { isPaginatedResponse } from "@/redux/typeGuards";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import { selectCurrentUser } from "@/redux/authentication/selectors";

const OrganizationProfileDetailsDisplay = ({
  organization,
  onEdit,
}: OrganizationDetailsContentProps) => {
  const { t } = useTranslation();
  const DOMPurify = createDOMPurify(window);
  const [showFullText, setShowFullText] = useState(false);

  const truncateHtmlText = (html: string, maxLength: number) => {
    if (!html) return { text: "", isTruncated: false };

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = DOMPurify.sanitize(html, {
      USE_PROFILES: { html: true },
    });
    const plainText = tempDiv.textContent || tempDiv.innerText || "";

    if (plainText.length <= maxLength) {
      return { text: html, isTruncated: false };
    }

    let truncatedText = plainText.substring(0, maxLength);
    const lastSpaceIndex = truncatedText.lastIndexOf(" ");
    if (lastSpaceIndex > maxLength * 0.8) {
      truncatedText = truncatedText.substring(0, lastSpaceIndex);
    }

    let htmlPosition = 0;
    let textPosition = 0;
    const htmlChars = html.split("");
    const textChars = truncatedText.split("");

    for (
      let i = 0;
      i < htmlChars.length && textPosition < textChars.length;
      i++
    ) {
      if (htmlChars[i] === textChars[textPosition]) {
        textPosition++;
      }
      htmlPosition = i;
    }

    const truncatedHtml = html.substring(0, htmlPosition) + "...";
    return { text: truncatedHtml, isTruncated: true };
  };

  const currentUser = useSelector(selectCurrentUser);
  const { canEdit } = useOrganizationPermissions(currentUser, organization);
  const achievementsResponse = useGetAchievementsQuery(
    { ids: organization.achievements, pagination: "off" },
    { skip: organization.achievements.length === 0 },
  );
  const communityRolesResponse = useGetCommunityRolesQuery(
    { ids: organization.communityRoles, pagination: "off" },
    { skip: organization.communityRoles.length === 0 },
  );
  const countryResponse = useGetCountryByIdQuery(organization.country);
  const languageResponse = useGetLanguageByIdQuery(
    organization.language as number,
    { skip: !organization.language },
  );

  const communicationStatus = organization.communicatingStatus
    ? OrganizationCommunicationStatuses.getItemByValue(
        organization.communicatingStatus,
      )
    : null;

  return (
    <div className="organization-details-content flex flex-col gap-400 full-height items-start">
      <div className="flex flex-col full-width">
        <div className="organization-cover-image-container full-width">
          <img
            src={organization.coverImage || placeholderImage}
            alt="Cover"
            className="organization-cover-image full-width full-height"
          />

          <BaseBadge
            leadingIcon={
              <BaseSvgRenderer
                component={DotIcon}
                width="24"
                height="24"
                color={communicationStatus?.color || "var(--neutral-400)"}
              />
            }
            borderColor="var(--neutral-300)"
            backgroundColor="var(--base-white)"
            fontColor="var(--neutral-900)"
            className="organization-status-badge"
          >
            {communicationStatus?.label || organization.communicatingStatus}
          </BaseBadge>
        </div>

        <div className="organization-profile-section flex flex-col gap-200">
          <div className="flex gap-200 justify-between items-end">
            <BaseAvatar
              imageUrl={organization.logo}
              label={organization.name}
              className="organization-logo"
            />

            {canEdit && (
              <BaseButton
                variant="link"
                size="small"
                leadingIcon={
                  <BaseSvgRenderer
                    component={EditIcon}
                    width="20"
                    height="20"
                    color="var(--neutral-900)"
                  />
                }
                onClick={onEdit}
              />
            )}
          </div>

          <h1 className="organization-name header-l-medium">
            {organization.name}
          </h1>

          <div className="flex flex-wrap gap-100">
            {achievementsResponse.isSuccess &&
            organization.achievements.length > 0 &&
            !isPaginatedResponse(achievementsResponse.data)
              ? achievementsResponse.data.map((achievement) => (
                  <BaseTooltip
                    title={achievement.label}
                    key={achievement.label}
                  >
                    <img
                      src={achievement.icon}
                      alt={achievement.label}
                      key={achievement.label}
                    />
                  </BaseTooltip>
                ))
              : null}

            {communityRolesResponse.isSuccess &&
            organization.communityRoles.length > 0 &&
            !isPaginatedResponse(communityRolesResponse.data)
              ? communityRolesResponse.data.map((communityRole) => (
                  <BaseTooltip
                    title={communityRole.label}
                    key={communityRole.label}
                  >
                    <img
                      src={communityRole.icon}
                      alt={communityRole.label}
                      key={communityRole.label}
                    />
                  </BaseTooltip>
                ))
              : null}
          </div>
        </div>
      </div>

      <div className="organization-section flex flex-col gap-200">
        <h3 className="section-title header-s-medium">
          {t("organizations.labels.generalInfo")}
        </h3>

        <div className="flex flex-col gap-200">
          <div className="info-item flex gap-200 items-center body-m-regular">
            <BaseSvgRenderer
              component={EmailIcon}
              width="20"
              height="20"
              stroke="var(--neutral-700)"
            />
            <BaseTooltip title={organization.email}>
              <span className="info-item-value info-item-ellipsis">
                {organization.email}
              </span>
            </BaseTooltip>
          </div>

          {organization.address && (
            <div className="info-item flex gap-200 items-center body-m-regular">
              <BaseSvgRenderer
                component={PinIcon}
                width="20"
                height="20"
                stroke="var(--neutral-700)"
              />
              <BaseTooltip title={organization.address}>
                <span className="info-item-value info-item-ellipsis">
                  {organization.address}
                </span>
              </BaseTooltip>
            </div>
          )}

          <div className="info-item flex gap-200 items-center body-m-regular">
            <BaseSvgRenderer
              component={WorldIcon}
              width="20"
              height="20"
              stroke="var(--neutral-700)"
            />
            <span className="info-item-value">
              {countryResponse.isSuccess ? (
                countryResponse.data.name
              ) : (
                <BaseSpinner size="small" />
              )}
            </span>
          </div>
          {organization.phoneNumber && (
            <div className="info-item flex gap-200 items-center body-m-regular">
              <BaseSvgRenderer
                component={PhoneIcon}
                width="20"
                height="20"
                stroke="var(--neutral-700)"
              />
              <span className="info-item-value">
                {organization.phoneNumber}
              </span>
            </div>
          )}
          {organization.language && (
            <div className="info-item flex gap-200 items-center body-m-regular">
              <BaseSvgRenderer
                component={LanguageIcon}
                width="20"
                height="20"
                stroke="var(--neutral-700)"
              />
              <span className="info-item-value">
                {languageResponse.isSuccess ? (
                  languageResponse.data.name
                ) : (
                  <BaseSpinner size="small" />
                )}
              </span>
            </div>
          )}
          {organization.chapter && (
            <div className="info-item flex gap-200 items-center body-m-regular">
              <BaseSvgRenderer
                component={FlagIcon}
                width="20"
                height="20"
                stroke="var(--neutral-700)"
              />
              <span className="info-item-value">
                {Chapters.getItemByValue(organization.chapter).label}
              </span>
            </div>
          )}
          {organization.parentOrganizationName && (
            <div className="info-item flex gap-200 items-center body-m-regular">
              <BaseSvgRenderer
                component={ParentOrganizationIcon}
                width="20"
                height="20"
                stroke="var(--neutral-700)"
              />
              <span className="info-item-value">
                {organization.parentOrganizationName}
              </span>
            </div>
          )}

          {currentUser && currentUser.role === Roles.ADMIN.value && (
            <div className="info-item flex gap-200 items-center body-m-regular">
              <BaseSvgRenderer
                component={IdCardIcon}
                width="20"
                height="20"
                stroke="var(--neutral-700)"
              />
              <span className="info-item-value">
                {hasExternalId(organization) && organization.externalId}
              </span>
            </div>
          )}
        </div>
      </div>

      {organization.website && (
        <>
          <BaseDivider className="organization-section-divider" />

          <div className="organization-section flex flex-col gap-200">
            <h3 className="section-title header-s-medium">
              {t("organizations.labels.officialSite")}
            </h3>
            <div className="flex flex-col gap-200">
              <div className="info-item flex gap-200 items-center body-m-regular">
                <BaseSvgRenderer
                  component={LinkIcon}
                  width="20"
                  height="20"
                  stroke="var(--neutral-700)"
                />
                <BaseTooltip title={organization.website}>
                  <a
                    href={organization.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="info-item-ellipsis"
                  >
                    {organization.website}
                  </a>
                </BaseTooltip>
              </div>
            </div>
          </div>
        </>
      )}

      {organization.aboutUs && (
        <>
          <BaseDivider className="organization-section-divider" />

          <div className="organization-section flex flex-col gap-200">
            <h3 className="section-title header-s-medium">
              {t("organizations.labels.about")}
            </h3>
            <div className="body-m-regular">
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    (() => {
                      const cleanedText =
                        organization.aboutUs?.replace(/&nbsp;/g, " ") || "";
                      const { text } = truncateHtmlText(cleanedText, 300);
                      return showFullText ? cleanedText : text;
                    })(),
                    {
                      USE_PROFILES: { html: true },
                    },
                  ),
                }}
              />
              {(() => {
                const cleanedText =
                  organization.aboutUs?.replace(/&nbsp;/g, " ") || "";
                const { isTruncated } = truncateHtmlText(cleanedText, 300);

                if (!isTruncated) return null;

                return (
                  <button
                    onClick={() => setShowFullText(!showFullText)}
                    className="show-more-btn"
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--primary-600)",
                      cursor: "pointer",
                      textDecoration: "underline",
                      fontSize: "14px",
                      marginTop: "8px",
                      padding: 0,
                    }}
                  >
                    {showFullText
                      ? t("buttons.showLess")
                      : t("buttons.showMore")}
                  </button>
                );
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrganizationProfileDetailsDisplay;
